import { useState } from 'react';
import {
  Box,
  Button,
  HStack,
  ListItem,
  OrderedList,
  Text,
} from '@chakra-ui/react';
import { AlertTriangle, Lock } from 'lucide-react';
import { useT, type MessageKey } from '../../i18n';
import type { KeyStorageState } from '../../lib/e2ee/session';

/**
 * Which explanation (docs/new-devices.md): `newDevice` — this device hasn't
 * been given the keys yet (D137); the others are `KeyStorageState` values
 * other than `ok` (D262): the browser blocks the site data the key lives
 * in, that storage is broken, it opened too slowly (reload; never "clear
 * site data"), the account added too many devices today, it is forgotten
 * when the site closes, the stored key couldn't be used and a fresh one
 * was made, the browser can't do the encryption at all, or the key is kept
 * but not working (sign out and in again).
 */
export type NewDeviceBannerVariant =
  | 'newDevice'
  | Exclude<KeyStorageState, 'ok'>;

const TEXT: Record<
  NewDeviceBannerVariant,
  { title: MessageKey; body: MessageKey }
> = {
  newDevice: { title: 'chat.newDevice.title', body: 'chat.newDevice.body' },
  blocked: {
    title: 'chat.keyStore.blocked.title',
    body: 'chat.keyStore.blocked.body',
  },
  broken: {
    title: 'chat.keyStore.broken.title',
    body: 'chat.keyStore.broken.body',
  },
  ephemeral: {
    title: 'chat.keyStore.ephemeral.title',
    body: 'chat.keyStore.ephemeral.body',
  },
  slow: { title: 'chat.keyStore.slow.title', body: 'chat.keyStore.slow.body' },
  tooMany: {
    title: 'chat.keyStore.tooMany.title',
    body: 'chat.keyStore.tooMany.body',
  },
  unsupported: {
    title: 'chat.keyStore.unsupported.title',
    body: 'chat.keyStore.unsupported.body',
  },
  unusable: {
    title: 'chat.keyStore.unusable.title',
    body: 'chat.keyStore.unusable.body',
  },
  replaced: {
    title: 'chat.keyStore.replaced.title',
    body: 'chat.keyStore.replaced.body',
  },
};

// Some messages in this chat are locked on this device: it hasn't been given
// their key yet (docs/new-devices.md, D137). One plain explanation instead of
// a puzzling line on every message; it goes by itself when keys arrive.
export function NewDeviceBanner({
  variant = 'newDevice',
}: {
  variant?: NewDeviceBannerVariant;
}) {
  const [open, setOpen] = useState(false);
  const t = useT();
  // The storage variants share one set of "How?" steps (browser settings);
  // a new or replaced key needs the family's devices, as D137 says; a slow
  // store or the day's device cap has nothing to expand.
  const storage =
    variant === 'blocked' || variant === 'broken' || variant === 'ephemeral';
  const text = TEXT[variant];
  const steps: MessageKey[] = storage
    ? ['chat.keyStore.step1', 'chat.keyStore.step2', 'chat.keyStore.step3']
    : variant === 'newDevice' || variant === 'replaced'
      ? ['chat.newDevice.step1', 'chat.newDevice.step2', 'chat.newDevice.step3']
      : [];
  const Icon =
    variant === 'newDevice' || variant === 'replaced' ? Lock : AlertTriangle;
  return (
    <Box
      mx={4}
      mt={2}
      mb={1}
      px={4}
      py={3}
      borderRadius="18px"
      bg="accent.tint"
      flexShrink={0}
      role="status"
      data-variant={variant}
    >
      <HStack align="flex-start" spacing={3}>
        <Box color="accent" pt="2px">
          <Icon size={18} aria-hidden="true" />
        </Box>
        <Box flex="1" minW={0}>
          <Text fontWeight={700} fontSize="15px">
            {t(text.title)}
          </Text>
          <Text fontSize="14px" color="ink.body" lineHeight="20px">
            {t(text.body)}
          </Text>
          {steps.length === 0 ? null : open ? (
            <OrderedList fontSize="14px" color="ink.body" mt={2} spacing={1}>
              {steps.map((step) => (
                <ListItem key={step}>{t(step)}</ListItem>
              ))}
            </OrderedList>
          ) : (
            <Button
              variant="link"
              size="sm"
              color="accent"
              mt={1}
              onClick={() => setOpen(true)}
            >
              {t('chat.newDevice.how')}
            </Button>
          )}
        </Box>
      </HStack>
    </Box>
  );
}
